import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useToast } from "../ui/use-toast";
import { useTheme } from "../ThemeProvider";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import Select from "react-select";
import { partsIndex } from "../../lib/algoliaClient";
import { debounce } from "lodash";
import PaymentMethodModal from "./PaymentMethodModal";
import StripePaymentModal from "./StripePaymentModal";
import { createFeaturedPart } from "../../store/membership/membershipActions";
import { updateFeaturePart } from "../../store/useParts/usePartsThunk";

const FeaturePartModal = ({
  isOpen,
  onClose,
  isLoading = false,
  onSuccess = () => {}
}) => {
  const { toast } = useToast();
  const { resolvedTheme } = useTheme();
  const { user, currentSubscription, defaultPaymentMethod } = useSelector(
    (state) => state.auth
  );
  const { isFeaturedPartLoading } = useSelector((state) => state.membership);
  const dispatch = useDispatch();

  const [selectedPart, setSelectedPart] = useState(null);
  const [weeks, setWeeks] = useState(1);
  const [parts, setParts] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isStripePaymentModalOpen, setIsStripePaymentModalOpen] =
    useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Clear state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setParts([]);
      setSelectedPart(null);
      setWeeks(1);
    }
  }, [isOpen]);

  const fetchParts = useCallback(
    async (query) => {
      if (!query || query.trim().length < 2) {
        setParts([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      try {
        const { hits } = await partsIndex.search(query.trim(), {
          filters: `sellerId:${user.uid}`,
          hitsPerPage: 10
        });
        // Filter out parts that are already featured (sortOrder = 1)
        const filteredHits = hits.filter((hit) => hit.sortOrder !== 1);

        const mappedParts = filteredHits.map((hit) => ({
          value: hit.objectID,
          label: hit.name_lowercase || "Unnamed Part",
          data: hit
        }));

        setParts(mappedParts);
      } catch (error) {
        console.error("Error searching parts:", error);
        // Only show error toast for actual search errors, not for empty results
        if (error.name !== "ApiError" || error.status !== 404) {
          toast({
            title: "Search Error",
            description: "Failed to search parts. Please try again.",
            variant: "destructive"
          });
        }
      } finally {
        setIsSearching(false);
      }
    },
    [user?.uid]
  );

  // Debounce search to reduce API calls
  const debouncedFetch = useMemo(
    () => debounce(fetchParts, 1000),
    [fetchParts]
  );

  useEffect(() => {
    // Only proceed if we have a valid user and search query
    if (!user?.uid) {
      setParts([]);
      return;
    }

    // Early return for invalid queries before debouncing
    if (!searchQuery || searchQuery.trim().length < 2) {
      setParts([]);
      return;
    }

    debouncedFetch(searchQuery);

    return () => {
      debouncedFetch.cancel();
    };
  }, [searchQuery, user?.uid, debouncedFetch]);

  // Custom styles for react-selects
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor:
        resolvedTheme === "dark" ? "hsla(224 71.4% 4.1%)" : "hsla(0 0% 100%)",
      borderColor:
        resolvedTheme === "dark"
          ? "hsla(215 27.9% 16.9%)"
          : "hsla(220 13% 91%)",
      color:
        resolvedTheme === "dark" ? "hsla(210 20% 98%)" : "hsla(224 71.4% 4.1%)",
      minHeight: "40px",
      boxShadow: state.isFocused
        ? resolvedTheme === "dark"
          ? "0 0 0 2px hsla(263.4 95.2% 66.1%)"
          : "0 0 0 2px hsla(262.1 83.3% 57.8%)"
        : provided.boxShadow,
      "&:hover": {
        borderColor:
          resolvedTheme === "dark"
            ? "hsla(215 27.9% 16.9%)"
            : "hsla(220 13% 91%)"
      }
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor:
        resolvedTheme === "dark"
          ? "hsla(224, 71.4%, 4.1%)"
          : "hsla(0, 0%, 100%)",
      border: `1px solid ${
        resolvedTheme === "dark" ? "hsla(220, 13%, 30%)" : "hsla(220, 13%, 80%)"
      }`,
      borderRadius: "6px",
      zIndex: 9999,
      position: "absolute",
      marginTop: "4px",
      maxHeight: "150px",
      overflowY: "auto"
    }),
    menuList: (provided) => ({
      ...provided,
      backgroundColor:
        resolvedTheme === "dark"
          ? "hsla(224, 71.4%, 4.1%)"
          : "hsla(0, 0%, 100%)",
      maxHeight: "150px",
      padding: "8px"
    }),

    menuList: (provided) => ({
      ...provided,
      backgroundColor:
        resolvedTheme === "dark" ? "hsla(224 71.4% 4.1%)" : "hsla(0 0% 100%)",
      maxHeight: "150px",
      padding: "8px"
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? resolvedTheme === "dark"
          ? "hsla(263.4 95.2% 66.1%)"
          : "hsla(262.1 83.3% 57.8%)"
        : state.isFocused
        ? resolvedTheme === "dark"
          ? "hsla(35 91.7% 55.5%)"
          : "hsla(30 94% 50%)"
        : resolvedTheme === "dark"
        ? "hsla(224 71.4% 4.1%)"
        : "hsla(0 0% 100%)",
      color:
        resolvedTheme === "dark" ? "hsla(210 20% 98%)" : "hsla(224 71.4% 4.1%)",
      "&:active": {
        backgroundColor:
          resolvedTheme === "dark" ? "hsla(224 71.4% 4.1%)" : "hsla(0 0% 100%)"
      },
      fontSize: "14px", // Ensure consistent font size
      padding: "8px 12px"
    }),
    input: (provided) => ({
      ...provided,
      color:
        resolvedTheme === "dark" ? "hsla(210 20% 98%)" : "hsla(224 71.4% 4.1%)"
    }),
    singleValue: (provided) => ({
      ...provided,
      color:
        resolvedTheme === "dark" ? "hsla(210 20% 98%)" : "hsla(224 71.4% 4.1%)"
    }),
    placeholder: (provided) => ({
      ...provided,
      color:
        resolvedTheme === "dark"
          ? "hsla(217.9 10.6% 64.9%)"
          : "hsla(220 8.9% 46.1%)"
    })
  };

  const handlePaymentMethod = () => {
    if (!defaultPaymentMethod?.id) {
      handleNewCardPayment();
    } else {
      setIsPaymentModalOpen(true);
    }
  };

  const handleModalClose = () => {
    if (!isProcessing) {
      setIsPaymentModalOpen(false);
    }
  };

  const handleExistingCardPayment = async () => {
    setIsProcessing(true);
    try {
      const payload = {
        userId: user.uid,
        partId: selectedPart.value,
        paymentMethodId: defaultPaymentMethod?.id,
        weeks: parseInt(weeks, 10)
      };

      await dispatch(createFeaturedPart({ payload })).unwrap();

      await dispatch(
        updateFeaturePart({ partId: selectedPart.value })
      ).unwrap();

      onSuccess?.();
    } catch (error) {
      console.error("Error processing payment:", error);
    } finally {
      setIsProcessing(false);
      setIsPaymentModalOpen(false);
    }
  };

  const handleNewCardPayment = () => {
    setIsStripePaymentModalOpen(true);
  };

  const handlePaymenSuccess = async (paymentMethodId) => {
    setIsProcessing(true);
    try {
      const payload = {
        userId: user.uid,
        partId: selectedPart.value,
        paymentMethodId: paymentMethodId,
        weeks: parseInt(weeks, 10)
      };

      await dispatch(createFeaturedPart({ payload })).unwrap();

      await dispatch(
        updateFeaturePart({ partId: selectedPart.value })
      ).unwrap();

      onSuccess?.();
    } catch (error) {
      console.error("Error processing payment:", error);
    } finally {
      setIsProcessing(false);
      setIsStripePaymentModalOpen(false);
      setIsPaymentModalOpen(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedPart) {
      toast({
        title: "Part Required",
        description: "Please select a part to feature.",
        variant: "destructive"
      });
      return;
    }

    if (weeks < 1 || weeks > 4) {
      toast({
        title: "Invalid Weeks",
        description: "Please enter a number of weeks between 1 and 4.",
        variant: "destructive"
      });
      return;
    }

    // Initiate payment process
    handlePaymentMethod();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md md:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold">
              Feature Part
            </DialogTitle>
            <DialogDescription className="text-sm">
              Select a part and set the number of weeks to feature it. Cost is
              $6 per week.
            </DialogDescription>
          </DialogHeader>

          <div className="p-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Parts Selection */}
              <div
                className="space-y-2"
                style={{ position: "relative", zIndex: 9999 }}
              >
                <Label htmlFor="partSelect">Select Part</Label>
                <div className="relative">
                  <Select
                    inputId="partSelect"
                    options={parts}
                    value={selectedPart}
                    onChange={setSelectedPart}
                    onInputChange={(value) => {
                      setSearchQuery(value);
                    }}
                    placeholder="Search for a part (min 2 characters)"
                    isSearchable
                    isClearable
                    isLoading={isSearching}
                    styles={customStyles}
                    components={{
                      DropdownIndicator: () => null,
                      IndicatorSeparator: () => null
                    }}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    noOptionsMessage={({ inputValue }) =>
                      !inputValue || inputValue.length < 2
                        ? "Type at least 2 characters to search"
                        : isSearching
                        ? "Searching..."
                        : "No parts found"
                    }
                  />
                </div>
              </div>
              {/* Weeks Input */}
              <div className="space-y-2">
                <Label htmlFor="weeks">Number of Weeks (1-4)</Label>
                <div className="relative">
                  <Input
                    id="weeks"
                    type="number"
                    min="1"
                    max="4"
                    placeholder="1"
                    required
                    value={weeks}
                    onChange={(e) => setWeeks(e.target.value)}
                    disabled={isLoading || isFeaturedPartLoading}
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  Total: ${6 * weeks}
                </p>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isFeaturedPartLoading || isLoading || !selectedPart}
              >
                {isFeaturedPartLoading ||
                  (isLoading && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ))}
                {isFeaturedPartLoading || isLoading
                  ? "Processing..."
                  : `Feature Part - $${6 * weeks}`}
              </Button>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      <PaymentMethodModal
        isOpen={isPaymentModalOpen}
        onClose={handleModalClose}
        onExistingCardSelect={handleExistingCardPayment}
        onNewCardSelect={handleNewCardPayment}
        isLoading={isProcessing || isFeaturedPartLoading}
      />

      <StripePaymentModal
        isOpen={isStripePaymentModalOpen}
        onClose={() => {
          setIsStripePaymentModalOpen(false);
        }}
        title={`Pay for Featured Part`}
        description={"Enter your card details to complete your purchase."}
        type="featuredPart"
        price={6 * weeks}
        frequency={"one-time"}
        buttonText={`Pay $${6 * weeks}`}
        onPaymentSuccess={handlePaymenSuccess}
        isLoading={isProcessing || isFeaturedPartLoading}
      />
    </>
  );
};

export default FeaturePartModal;
